/**
 * ReachCard (COMMS-UX-01, 2026-10-09) — "How we may reach them", the ONE
 * truth block at the top of the 360 Messages tab.
 *
 * Born from Seth opening 1015846 (full do-not-contact) and finding: a legacy
 * "Marketing email · Opted in" line (read from empty email_suppression), a
 * cryptic red bell that actually held the truth, and confident Send text /
 * Send email buttons beside it. Three sources, three stories, one tab.
 *
 * This card reads the SAME two stores the sending gate honors — the office
 * switches (pegasus_comms_pref) and the consent ledger (contact_consent) —
 * and says the state in words, with the reason: "Off — office preference
 * (ESC note: mail paper receipts…)". The ServiceTitan/HubSpot pattern:
 * status-first language, one edit surface, no icon riddles.
 *
 * Language rule: "opted in" appears NOWHERE here. Marketing email is either
 * "never objected" (our default-allowed truth) or "unsubscribed (when)".
 */
import { useMemo, useState } from "react";
import { Mail, MessageSquare, Phone, Printer, Pencil } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { cn } from "@/lib/cn";
import { useCommsPref } from "@/features/comms/comms";
import { CommsPrefSwitches } from "@/features/comms/CommsPrefButton";
import { useEditCustomer } from "./editCustomerContext";
import { useCustomerConsent } from "@/features/comms/consent";

type RowState = "ok" | "off" | "partial";

function StateChip({ state }: { state: RowState }) {
  // DESIGN-CALM: neutral when everything is normal — color marks only the
  // states that change how the office behaves.
  if (state === "ok") return <Badge>OK</Badge>;
  if (state === "off") return <Badge tone="bad">Off</Badge>;
  return <Badge tone="warn">Limited</Badge>;
}

export function ReachCard({ custno }: { custno: string }) {
  const { data: pref } = useCommsPref(custno);
  const consent = useCustomerConsent(custno);
  const [editing, setEditing] = useState(false);
  const edit = useEditCustomer(); // EDIT-SUITE-01

  const view = useMemo(() => {
    const noSms = pref?.no_sms ?? false;
    const noEmail = pref?.no_email ?? false;
    const paper = pref?.paper_invoice ?? false;
    const noMkt = pref?.no_marketing_email ?? false;

    // A texted STOP is phone-level and final — surface it even when the
    // office switches are open.
    const stops = consent.rows.filter((r) => r.channel === "sms" && r.status === "stop");
    const stopNums = [...new Set(stops.map((r) => r.endpoint))].map((e) =>
      e.length === 10 ? `(${e.slice(0, 3)}) ${e.slice(3, 6)}-${e.slice(6)}` : e,
    );

    // Marketing email truth: the account-level consent row, else default-allowed.
    const mktRow = consent.rows.find((r) => r.channel === "email" && r.endpoint === "*" && r.purpose === "marketing");
    const mktOff = noEmail || noMkt || mktRow?.status === "unsubscribed";
    const mktWhen = mktRow?.changed_at
      ? new Date(mktRow.changed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : null;

    const text: { state: RowState; reason: string } = noSms
      ? { state: "off", reason: "Office preference — no texts to this customer." }
      : stops.length > 0
        ? { state: "partial", reason: `Texted STOP from ${stopNums.join(", ")} — that number is closed for good (only a texted START reopens it).` }
        : { state: "ok", reason: "Texts are fine — job updates and replies send normally." };

    const email: { state: RowState; reason: string } = noEmail
      ? { state: "off", reason: "Office preference — no email to this customer." }
      : paper
        ? { state: "partial", reason: "Invoices go by MAIL (paper) — appointment and job emails still send." }
        : { state: "ok", reason: "Email is fine — invoices, receipts and job updates send normally." };

    const onlyPhone = noSms && noEmail;
    return { noSms, noEmail, paper, mktOff, mktWhen, text, email, onlyPhone };
  }, [pref, consent.rows]);

  const Row = ({
    icon: Icon, name, state, reason, children,
  }: {
    icon: React.ComponentType<{ className?: string }>; name: string;
    state?: RowState; reason: string; children?: React.ReactNode;
  }) => (
    <div className="flex items-start gap-3 px-4 py-2.5">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-800">{name}</span>
          {state && <StateChip state={state} />}
        </div>
        <p className="mt-0.5 text-xs leading-snug text-slate-500">{reason}</p>
        {children}
      </div>
    </div>
  );

  return (
    <div className="rounded-xl border border-hairline bg-white">
      <div className="flex items-center gap-2 border-b border-hairline/70 px-4 py-2.5">
        <h3 className="text-sm font-semibold text-slate-900">How we may reach them</h3>
        <span className="relative ml-auto">
          <button
            type="button"
            onClick={() => (edit.available ? edit.open("reach") : setEditing((o) => !o))}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold transition-colors",
              editing ? "border-brand-300 bg-brand-50 text-brand-700" : "border-hairline bg-white text-slate-600 hover:bg-slate-50",
            )}
          >
            <Pencil className="h-3 w-3" /> Edit
          </button>
          {editing && (
            <>
              <button type="button" aria-hidden tabIndex={-1} onClick={() => setEditing(false)} className="fixed inset-0 z-10 cursor-default" />
              <div className="absolute right-0 top-full z-20 mt-1 w-72 overflow-hidden rounded-lg border border-hairline bg-white py-1 shadow-lg">
                <CommsPrefSwitches custno={custno} />
              </div>
            </>
          )}
        </span>
      </div>

      <div className="divide-y divide-hairline/70">
        <Row icon={MessageSquare} name="Text" state={view.text.state} reason={view.text.reason} />
        <Row icon={Mail} name="Email" state={view.email.state} reason={view.email.reason}>
          {!view.noEmail && (
            <p className="mt-0.5 text-[11px] text-slate-400">
              Marketing:{" "}
              {view.mktOff
                ? <span className="font-medium text-slate-500">unsubscribed{view.mktWhen ? ` ${view.mktWhen}` : ""} — newsletters and offers never send</span>
                : <span>never objected — marketing emails may send</span>}
            </p>
          )}
        </Row>
        <Row
          icon={Phone}
          name="Phone"
          reason={view.onlyPhone
            ? "Calls are fine — with text and email off, the phone (and mail) is how this customer is reached."
            : "Calls are always fine — Pegasus never auto-dials; this is for humans."}
        />
        {view.paper && (
          <Row
            icon={Printer}
            name="Mail"
            reason="Paper invoices — the office prints & mails each finalized invoice (it lands on the To-Mail list on the Invoices page)."
          />
        )}
      </div>

      {pref?.note && (
        <div className="border-t border-hairline bg-slate-50/60 px-4 py-2 text-[11px] italic leading-snug text-slate-500">
          “{pref.note}”
        </div>
      )}
    </div>
  );
}
